import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  Loader2,
  CheckCircle2,
  Edit3,
  Sparkles,
} from "lucide-react";

/**
 * Internal AI assistant for admin & staff.
 * - Admins can ask SofIA to create a reservation; she returns a draft and a
 *   confirmation card appears. On click, the reservation is persisted.
 */
export default function InternalAIChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(
    () => `${user?.id || "u"}-${Math.random().toString(36).slice(2, 10)}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0 && user) {
      setMessages([
        {
          role: "assistant",
          text:
            user.role === "admin"
              ? "¡Hola! Soy SofIA. Puedo ayudarte con procedimientos, políticas, plantillas y también crear reservas. Dime qué necesitas."
              : "¡Hola! Soy SofIA. Puedo ayudarte con procedimientos, políticas y plantillas de mensajes para clientes. ¿Qué necesitas?",
          ts: Date.now(),
        },
      ]);
    }
  }, [open, messages.length, user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Only visible to admin & staff
  if (!user || !["admin", "staff"].includes(user.role)) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text, ts: Date.now() }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/internal-chat", {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: data.reply,
          ts: Date.now(),
          pending_action: data.pending_action || null,
        },
      ]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const confirmBooking = async (idx, draft) => {
    try {
      const { data } = await api.post("/ai/internal-chat/confirm-booking", { draft });
      toast.success(`Reserva creada: ${data.booking_number}`);
      // Mark the message's action as completed
      setMessages((m) =>
        m.map((msg, i) =>
          i === idx
            ? { ...msg, pending_action: { ...msg.pending_action, created: data } }
            : msg
        )
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const cancelDraft = (idx) => {
    setMessages((m) =>
      m.map((msg, i) =>
        i === idx ? { ...msg, pending_action: { ...msg.pending_action, cancelled: true } } : msg
      )
    );
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="internal-ai-fab"
          aria-label="Abrir SofIA interna"
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#F5B301] text-black shadow-2xl hover:bg-black hover:text-[#F5B301] transition-colors flex items-center justify-center border-2 border-black"
        >
          <Sparkles size={22} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[520px] h-[720px] max-h-[calc(100vh-2.5rem)] bg-white border-2 border-black shadow-2xl flex flex-col"
          data-testid="internal-ai-panel"
        >
          <header className="bg-black text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#F5B301] text-black flex items-center justify-center">
                <Bot size={16} />
              </div>
              <div>
                <div className="text-sm font-display font-bold leading-none">SofIA interna</div>
                <div className="text-[9px] uppercase tracking-widest text-white/70 mt-1">
                  Equipo DS Travel Tours
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              data-testid="internal-ai-close"
              className="w-7 h-7 flex items-center justify-center hover:bg-white/10"
            >
              <X size={16} />
            </button>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-3 bg-zinc-50"
            data-testid="internal-ai-messages"
          >
            {messages.map((m, i) => (
              <div key={i}>
                <div
                  className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}
                >
                  {m.role === "assistant" && (
                    <div className="w-7 h-7 bg-black text-white flex items-center justify-center shrink-0">
                      <Bot size={13} />
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-black text-white"
                        : "bg-white border border-zinc-200"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
                {m.pending_action?.type === "create_booking" &&
                  m.pending_action.draft &&
                  !m.pending_action.created &&
                  !m.pending_action.cancelled && (
                    <BookingDraftCard
                      draft={m.pending_action.draft}
                      onConfirm={() => confirmBooking(i, m.pending_action.draft)}
                      onCancel={() => cancelDraft(i)}
                    />
                  )}
                {m.pending_action?.created && (
                  <div
                    className="mt-2 ml-9 border-2 border-emerald-600 bg-emerald-50 px-3 py-2 text-sm flex items-center gap-2"
                    data-testid="internal-ai-booking-created"
                  >
                    <CheckCircle2 size={16} className="text-emerald-700" />
                    Reserva <b>{m.pending_action.created.booking_number}</b> creada.
                  </div>
                )}
                {m.pending_action?.cancelled && (
                  <div className="mt-2 ml-9 text-xs text-zinc-500">
                    Borrador descartado.
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-2">
                <div className="w-7 h-7 bg-black text-white flex items-center justify-center">
                  <Bot size={13} />
                </div>
                <div className="bg-white border border-zinc-200 px-3 py-2 flex items-center gap-2 text-sm">
                  <Loader2 className="animate-spin" size={13} /> Pensando…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-black p-2 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder={
                user.role === "admin"
                  ? "Ej. Crea una reserva de Juan +34611… de T4 al Hotel Riu el 15/12 10:00"
                  : "Pregúntame cualquier procedimiento…"
              }
              className="flex-1 h-10 px-3 text-sm border border-black rounded-none"
              disabled={sending}
              data-testid="internal-ai-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="w-10 h-10 bg-black text-white flex items-center justify-center disabled:opacity-40"
              data-testid="internal-ai-send"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function BookingDraftCard({ draft, onConfirm, onCancel }) {
  const rows = [
    ["Cliente", draft.client_name],
    ["Teléfono", draft.client_phone],
    ["Email", draft.client_email || "—"],
    ["Fecha", `${draft.pickup_date} ${draft.pickup_time}`],
    ["Origen", draft.pickup_location],
    ["Destino", draft.dropoff_location],
    ["Pasajeros", draft.passengers || 1],
    ["Vehículo", draft.vehicle_type || "standard"],
    ["Vuelo", draft.flight_number || "—"],
    ["Proveedor", draft.provider || "ds_travel_tours"],
    ["Precio", draft.price != null ? `${draft.price} €` : "— (se calcula)"],
  ];
  return (
    <div
      className="mt-2 ml-9 border-2 border-black bg-white"
      data-testid="internal-ai-booking-draft"
    >
      <div className="bg-[#F5B301] text-black px-3 py-2 flex items-center gap-2">
        <Edit3 size={14} />
        <div className="text-[10px] uppercase tracking-widest font-bold">
          Borrador de reserva — confirma para guardar
        </div>
      </div>
      <dl className="divide-y divide-zinc-200">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3 px-3 py-1.5 text-xs">
            <dt className="text-zinc-500 w-20 shrink-0">{k}</dt>
            <dd className="flex-1 break-words">{String(v)}</dd>
          </div>
        ))}
      </dl>
      <div className="flex gap-2 p-2 border-t border-black">
        <button
          onClick={onCancel}
          className="flex-1 h-9 border-2 border-black text-[10px] uppercase tracking-widest hover:bg-zinc-100"
          data-testid="internal-ai-booking-cancel"
        >
          Descartar
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 h-9 bg-black text-white text-[10px] uppercase tracking-widest hover:bg-[#F5B301] hover:text-black transition-colors"
          data-testid="internal-ai-booking-confirm"
        >
          <CheckCircle2 size={12} className="inline mr-1" /> Confirmar
        </button>
      </div>
    </div>
  );
}
